import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  useColorMode,
  useToken,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import JSZip from 'jszip';
import {
  useGetJobQuery,
  useSeparateStemsMutation,
  useCacheStemNamesMutation,
} from '../redux/services/mixgenie';
import { useAuth } from '../auth/AuthProvider';

/**
 * Studio — a light-touch DAW mixer over N stems.
 *
 * Stem source: Mureka's `/v1/song/stem` via PromptLab. Mureka packages the
 * split into a single ZIP:
 *   - audio-separation-1 → 5 stems  (instrumental, vocals, drums, bass, other)
 *   - audio-separation-2 → 12 stems (vocals, drums, bass, guitar, piano, flute,
 *                                    FX, strings, synth, keyboard, brass, other)
 *   - audio-separation-3 → 2 stems  (vocal, accompaniment)
 *
 * The BE stores just the ZIP URL + the model choice. We fetch the ZIP, unzip
 * client-side (JSZip), decode each audio entry into a shared AudioContext,
 * and render a mixer with:
 *   - N channel strips (grid adapts: 2/3/4/6 columns depending on count)
 *   - per-stem canvas waveform (peaks precomputed at decode)
 *   - vertical volume slider + Mute + Solo
 *   - master transport (play/pause/scrub) with sample-accurate sync via
 *     freshly-scheduled BufferSourceNodes on every play/seek
 *
 * Two entry points:
 *   - `/studio/<jobId>` from an existing song (My Songs / composer Result)
 *   - `/studio/<uploadJobId>` from the DropCard "Split stems" button (BE
 *      creates a synthetic upload-only job whose only content is the file
 *      the user picked; both flows converge here).
 */

// Well-known stem name → colour+icon mapping. Anything not listed gets a
// deterministic default hue keyed on the string hash so the palette stays
// distinct across 12 channels without being random.
const STEM_ICONS: Record<string, string> = {
  vocals: '🎤', vocal: '🎤',
  drums: '🥁', drum: '🥁',
  bass: '🎸',
  guitar: '🎸',
  piano: '🎹', keyboard: '🎹',
  flute: '🪈', strings: '🎻',
  synth: '🎛',
  brass: '🎺', woodwinds: '🎷', 'brass-woodwinds': '🎺',
  fx: '✨', effects: '✨',
  instrumental: '🎶',
  accompaniment: '🎶',
  other: '🎹', others: '🎹', rest: '🎹',
};

const STEM_HUES: Record<string, string> = {
  vocals: '#ec4899', vocal: '#ec4899',
  drums: '#f59e0b', drum: '#f59e0b',
  bass: '#8b5cf6',
  guitar: '#f97316',
  piano: '#06b6d4', keyboard: '#06b6d4',
  flute: '#84cc16', strings: '#a855f7',
  synth: '#22d3ee',
  brass: '#eab308', woodwinds: '#65a30d', 'brass-woodwinds': '#ca8a04',
  fx: '#e879f9', effects: '#e879f9',
  instrumental: '#38bdf8',
  accompaniment: '#38bdf8',
  other: '#94a3b8', others: '#94a3b8', rest: '#94a3b8',
};

function hueFromString(s: string): string {
  // Simple djb2 hash → HSL. Only used as fallback for unknown stem names
  // so 12-channel splits get distinct colours even when Mureka names shift.
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

function labelForStem(rawName: string): string {
  const clean = rawName.replace(/\.[a-z0-9]+$/i, '').replace(/[_\-]+/g, ' ').trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function normalizeStemKey(rawName: string): string {
  return rawName
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_\-\s]+/g, '-')
    .toLowerCase();
}

function fmtTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function computePeaks(buffer: AudioBuffer, targetBins = 200): number[] {
  const channelData = buffer.getChannelData(0);
  const binSize = Math.max(1, Math.floor(channelData.length / targetBins));
  const peaks: number[] = [];
  for (let bin = 0; bin < targetBins; bin++) {
    let peak = 0;
    const start = bin * binSize;
    const end = Math.min(start + binSize, channelData.length);
    for (let i = start; i < end; i++) {
      const v = Math.abs(channelData[i]);
      if (v > peak) peak = v;
    }
    peaks.push(peak);
  }
  return peaks;
}

function drawWaveform(
  canvas: HTMLCanvasElement | null,
  peaks: number[] | null,
  progress: number,
  playedColor: string,
  aheadColor: string,
) {
  if (!canvas || !peaks?.length) return;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = Math.round(rect.width * dpr);
  const h = Math.round(rect.height * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  const barWidth = w / peaks.length;
  const mid = h / 2;
  const progressBin = Math.floor(progress * peaks.length);
  for (let i = 0; i < peaks.length; i++) {
    const p = peaks[i];
    const barH = Math.max(2 * dpr, p * (h * 0.92));
    const x = i * barWidth;
    ctx.fillStyle = i <= progressBin ? playedColor : aheadColor;
    ctx.fillRect(x + barWidth * 0.15, mid - barH / 2, barWidth * 0.7, barH);
  }
}

interface DecodedStem {
  key: string;      // normalized (lowercase, dashes)
  label: string;    // display label
  icon: string;
  hue: string;
  buffer: AudioBuffer;
  peaks: number[];
}

export default function StudioPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user?.email;
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [separateStems, { isLoading: isSubmittingSplit }] = useSeparateStemsMutation();
  const [cacheStemNames] = useCacheStemNamesMutation();
  const { data: jobResp, error: jobError, refetch } = useGetJobQuery(jobId!, {
    skip: !jobId,
    pollingInterval: 3000,
  });
  const job = jobResp?.data;
  const stemStatus: 'idle' | 'processing' | 'ready' | 'failed' =
    job?.stems?.status || 'idle';
  const stemZipUrl: string | undefined = job?.stems?.zipUrl;
  const cachedStemNames: string[] | undefined = job?.stems?.stemNames;

  // Web Audio graph
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainNodesRef = useRef<Record<string, GainNode | null>>({});
  const sourceNodesRef = useRef<Record<string, AudioBufferSourceNode | null>>({});
  const [decoded, setDecoded] = useState<DecodedStem[]>([]);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [decodeProgress, setDecodeProgress] = useState<{ done: number; total: number } | null>(null);

  const [volumes, setVolumes] = useState<Record<string, number>>({});
  const [muted, setMuted] = useState<Record<string, boolean>>({});
  const [soloed, setSoloed] = useState<Record<string, boolean>>({});

  const [isPlaying, setIsPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const startedAtCtxTimeRef = useRef(0);
  const startedFromOffsetRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  const canvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});

  const [hotAccent] = useToken('colors', ['hot.500']);

  const duration = useMemo(() => {
    if (!decoded.length) return 0;
    return Math.max(...decoded.map((d) => d.buffer.duration));
  }, [decoded]);

  const allReady = decoded.length > 0 && !decoding;

  const channelGain = useCallback(
    (key: string): number => {
      const anySoloed = Object.values(soloed).some(Boolean);
      if (anySoloed && !soloed[key]) return 0;
      if (muted[key]) return 0;
      return volumes[key] ?? 1;
    },
    [muted, soloed, volumes],
  );

  // Sync GainNode values on M/S/volume change.
  useEffect(() => {
    for (const stem of decoded) {
      const node = gainNodesRef.current[stem.key];
      if (node) {
        const ctx = audioCtxRef.current;
        const target = channelGain(stem.key);
        if (ctx) {
          node.gain.cancelScheduledValues(ctx.currentTime);
          node.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.03);
        } else {
          node.gain.value = target;
        }
      }
    }
  }, [channelGain, decoded]);

  // Fetch + unzip + decode when the job flips to ready.
  //
  // Only re-run when the ZIP URL actually changes (not on every RTK poll —
  // the container object reference changes each 3s tick but the URL string
  // stays identical while the job is ready; primitive-string dep prevents
  // the cleanup from cancelling in-flight decodes).
  useEffect(() => {
    if (stemStatus !== 'ready' || !stemZipUrl) return;
    if (decoded.length > 0) return;
    let cancelled = false;
    setDecoding(true);
    setDecodeError(null);
    setDecodeProgress(null);

    (async () => {
      try {
        const AudioCtor: typeof AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtor();
        const ctx = audioCtxRef.current;

        const resp = await fetch(stemZipUrl, { credentials: 'omit' });
        if (!resp.ok) throw new Error(`Failed to fetch stem archive (${resp.status})`);
        const zipBytes = await resp.arrayBuffer();
        if (cancelled) return;
        const zip = await JSZip.loadAsync(zipBytes);
        if (cancelled) return;

        // Discover audio entries — Mureka's ZIP nests them at the root or
        // under a folder. Skip MIDI / json / non-audio.
        const audioEntries = Object.values(zip.files).filter((f) => {
          if (f.dir) return false;
          const name = f.name.toLowerCase();
          if (/\/$/.test(name)) return false;
          if (/(^|\/)__macosx\//i.test(name)) return false;
          return /\.(wav|mp3|m4a|flac|ogg)$/i.test(name);
        });
        if (!audioEntries.length) throw new Error('No audio tracks found in the stem archive');

        // Preserve Mureka's canonical stem ordering so the mixer reads
        // naturally left-to-right (vocals, drums, bass, ...) rather than
        // whatever alphabetical order the ZIP iterates in.
        const ORDER = ['vocals', 'vocal', 'drums', 'bass', 'guitar', 'piano', 'keyboard', 'strings', 'synth', 'flute', 'brass', 'woodwinds', 'brass-woodwinds', 'fx', 'effects', 'instrumental', 'accompaniment', 'other', 'others', 'rest'];
        audioEntries.sort((a, b) => {
          const ak = normalizeStemKey(a.name.split('/').pop() || a.name);
          const bk = normalizeStemKey(b.name.split('/').pop() || b.name);
          const ai = ORDER.indexOf(ak);
          const bi = ORDER.indexOf(bk);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;
          return ak.localeCompare(bk);
        });

        setDecodeProgress({ done: 0, total: audioEntries.length });

        // Decode sequentially — parallel decodes can throttle on some
        // browsers, and sequential progress is nicer for the UI.
        const result: DecodedStem[] = [];
        const nextVolumes: Record<string, number> = {};
        const nextMuted: Record<string, boolean> = {};
        const nextSoloed: Record<string, boolean> = {};
        for (const entry of audioEntries) {
          if (cancelled) return;
          const fileName = entry.name.split('/').pop() || entry.name;
          const key = normalizeStemKey(fileName);
          const label = labelForStem(fileName);
          const icon = STEM_ICONS[key] || '🎧';
          const hue = STEM_HUES[key] || hueFromString(key);
          const arr = await entry.async('arraybuffer');
          if (cancelled) return;
          const buffer = await ctx.decodeAudioData(arr.slice(0));
          const peaks = computePeaks(buffer, 200);
          const gain = ctx.createGain();
          gain.gain.value = 1;
          gain.connect(ctx.destination);
          gainNodesRef.current[key] = gain;
          nextVolumes[key] = 1;
          nextMuted[key] = false;
          nextSoloed[key] = false;
          result.push({ key, label, icon, hue, buffer, peaks });
          setDecodeProgress({ done: result.length, total: audioEntries.length });
        }
        if (cancelled) return;
        setDecoded(result);
        setVolumes(nextVolumes);
        setMuted(nextMuted);
        setSoloed(nextSoloed);
        // Cache the stem names on the job so subsequent visits render the
        // channel labels without needing to peek at the ZIP first. Only
        // POST if we picked up different names than what's cached.
        const namesNow = result.map((r) => r.key);
        const same =
          cachedStemNames &&
          cachedStemNames.length === namesNow.length &&
          cachedStemNames.every((n, i) => n === namesNow[i]);
        if (jobId && !same) {
          cacheStemNames({ jobId, names: namesNow }).unwrap().catch(() => undefined);
        }
      } catch (err: any) {
        if (cancelled) return;
        setDecodeError(err?.message || 'Failed to load stems');
      } finally {
        if (!cancelled) setDecoding(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stemStatus, stemZipUrl]);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      try {
        for (const key of Object.keys(sourceNodesRef.current)) {
          sourceNodesRef.current[key]?.stop();
          sourceNodesRef.current[key]?.disconnect();
        }
        for (const key of Object.keys(gainNodesRef.current)) {
          gainNodesRef.current[key]?.disconnect();
        }
        audioCtxRef.current?.close();
      } catch {
        /* ignore */
      }
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  // Redraw waveforms whenever the playhead moves or theme flips.
  useEffect(() => {
    if (!allReady) return;
    const dur = duration || 1;
    const progress = Math.min(1, Math.max(0, playhead / dur));
    for (const stem of decoded) {
      drawWaveform(
        canvasRefs.current[stem.key],
        stem.peaks,
        progress,
        stem.hue,
        isDark ? 'rgba(237,231,245,0.14)' : 'rgba(30,26,46,0.14)',
      );
    }
  }, [playhead, isDark, decoded, allReady, duration]);

  // Playhead RAF loop.
  useEffect(() => {
    if (!isPlaying) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }
    const step = () => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const t = ctx.currentTime - startedAtCtxTimeRef.current + startedFromOffsetRef.current;
      if (t >= duration) {
        stopPlayback();
        setPlayhead(0);
        return;
      }
      setPlayhead(t);
      rafIdRef.current = requestAnimationFrame(step);
    };
    rafIdRef.current = requestAnimationFrame(step);
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, duration]);

  const startPlayback = useCallback(
    (fromOffset: number) => {
      const ctx = audioCtxRef.current;
      if (!ctx || !allReady) return;
      if (ctx.state === 'suspended') void ctx.resume();
      for (const stem of decoded) {
        const prev = sourceNodesRef.current[stem.key];
        if (prev) {
          try { prev.stop(); } catch { /* ignore */ }
          try { prev.disconnect(); } catch { /* ignore */ }
          sourceNodesRef.current[stem.key] = null;
        }
      }
      const startAt = ctx.currentTime + 0.05;
      for (const stem of decoded) {
        const src = ctx.createBufferSource();
        src.buffer = stem.buffer;
        const gain = gainNodesRef.current[stem.key];
        if (gain) src.connect(gain);
        else src.connect(ctx.destination);
        src.start(startAt, fromOffset);
        sourceNodesRef.current[stem.key] = src;
      }
      startedAtCtxTimeRef.current = startAt;
      startedFromOffsetRef.current = fromOffset;
      setIsPlaying(true);
    },
    [allReady, decoded],
  );

  const stopPlayback = useCallback(() => {
    for (const key of Object.keys(sourceNodesRef.current)) {
      const prev = sourceNodesRef.current[key];
      if (prev) {
        try { prev.stop(); } catch { /* ignore */ }
        try { prev.disconnect(); } catch { /* ignore */ }
        sourceNodesRef.current[key] = null;
      }
    }
    setIsPlaying(false);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!allReady) return;
    if (isPlaying) {
      const ctx = audioCtxRef.current;
      if (ctx) {
        const t = ctx.currentTime - startedAtCtxTimeRef.current + startedFromOffsetRef.current;
        setPlayhead(Math.min(duration, Math.max(0, t)));
      }
      stopPlayback();
    } else {
      startPlayback(playhead >= duration ? 0 : playhead);
    }
  }, [allReady, duration, isPlaying, playhead, startPlayback, stopPlayback]);

  const seekTo = useCallback(
    (t: number) => {
      const target = Math.min(duration, Math.max(0, t));
      setPlayhead(target);
      if (isPlaying) startPlayback(target);
    },
    [duration, isPlaying, startPlayback],
  );

  // Split-depth choice for THIS study session — the user picks 2/5/12 in
  // the SplitCta card before hitting Split. Default matches the composer.
  const [pickedStemModel, setPickedStemModel] = useState<'audio-separation-1' | 'audio-separation-2' | 'audio-separation-3'>('audio-separation-2');
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const handleSplit = useCallback(async () => {
    if (!isAuthed) {
      navigate(`/login?next=/studio/${jobId}`);
      return;
    }
    if (!jobId) return;
    setTriggerError(null);
    try {
      await separateStems({ jobId, stemModel: pickedStemModel }).unwrap();
      refetch();
    } catch (err: any) {
      const code = err?.data?.code;
      if (code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
        setTriggerError('Out of credits — top up in Credits to split more songs.');
      } else if (err?.status === 503) {
        setTriggerError('Stem separation is temporarily unavailable — try again in a moment.');
      } else {
        setTriggerError(err?.data?.message || 'Failed to start stem separation.');
      }
    }
  }, [isAuthed, jobId, navigate, refetch, separateStems, pickedStemModel]);

  // ---------------- Views ----------------

  if (!jobId) {
    return (
      <Center minH="100vh" bg="mg.bg">
        <Text color="mg.muted">No song selected.</Text>
      </Center>
    );
  }

  // Responsive column count: scale down to keep controls tappable.
  // 2 stems → 2 cols · 3-5 → up to 3 cols on mobile / 5 on desktop
  //  · 6-12 → up to 3 cols on mobile / 4-6 on desktop.
  const n = decoded.length || 0;
  const gridColumns = {
    base: n <= 2 ? '1fr 1fr' : n <= 6 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
    md: n <= 4 ? `repeat(${Math.max(2, n)}, 1fr)` : n <= 6 ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
    lg: n <= 6 ? `repeat(${Math.max(2, n)}, 1fr)` : n <= 8 ? 'repeat(4, 1fr)' : 'repeat(6, 1fr)',
  };

  return (
    <Box minH="100vh" bg="mg.bg" color="mg.fg">
      {/* Top bar */}
      <Flex
        align="center"
        justify="space-between"
        px={{ base: 4, md: 6 }}
        py={3}
        borderBottom="1px solid"
        borderColor="mg.line"
        bg="mg.surface"
        position="sticky"
        top={0}
        zIndex={2}
      >
        <IconButton
          aria-label="Back"
          icon={<span aria-hidden style={{ fontSize: 18 }}>←</span>}
          onClick={() => navigate(-1)}
          variant="ghost"
          size="sm"
        />
        <VStack spacing={0} align="center">
          <Text fontSize="xs" color="mg.muted" letterSpacing="wide" textTransform="uppercase">
            Studio
          </Text>
          <Text fontSize="sm" fontWeight={600} noOfLines={1} maxW="60vw">
            {job?.title || 'Untitled'}
          </Text>
        </VStack>
        <IconButton
          aria-label="Close"
          icon={<span aria-hidden style={{ fontSize: 18 }}>×</span>}
          onClick={() => navigate('/composer')}
          variant="ghost"
          size="sm"
        />
      </Flex>

      {/* Workspace */}
      <Box px={{ base: 3, md: 6 }} py={{ base: 4, md: 8 }} maxW="1180px" mx="auto">
        {jobError ? (
          <StatusCard tone="error">Couldn't load this song.</StatusCard>
        ) : !job ? (
          <StatusCard>
            <Spinner size="sm" mr={3} />
            Loading song…
          </StatusCard>
        ) : stemStatus === 'idle' || stemStatus === 'failed' ? (
          <SplitCta
            onSplit={handleSplit}
            isSubmitting={isSubmittingSplit}
            failedReason={stemStatus === 'failed' ? job?.stems?.errorMessage : null}
            error={triggerError}
            pickedModel={pickedStemModel}
            onPickModel={setPickedStemModel}
          />
        ) : stemStatus === 'processing' ? (
          <ProcessingCard requestedAt={job?.stems?.requestedAt} />
        ) : decodeError ? (
          <StatusCard tone="error">
            <VStack align="start" spacing={2}>
              <Text>Couldn't load the stems.</Text>
              <Text fontSize="xs" color="mg.muted">{decodeError}</Text>
              <Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>
            </VStack>
          </StatusCard>
        ) : !allReady ? (
          <StatusCard>
            <Spinner size="sm" mr={3} />
            {decodeProgress
              ? `Loading tracks into the mixer… ${decodeProgress.done}/${decodeProgress.total}`
              : 'Loading tracks into the mixer…'}
          </StatusCard>
        ) : (
          <>
            <Text fontSize="xs" color="mg.dim" mb={3} textAlign="center">
              {n} stems · {job?.stems?.model || 'audio-separation-2'}
            </Text>
            <Box display="grid" gridTemplateColumns={gridColumns} gap={{ base: 3, md: 4 }} mb={6}>
              {decoded.map((stem) => (
                <ChannelStrip
                  key={stem.key}
                  stem={stem}
                  volume={volumes[stem.key] ?? 1}
                  onVolume={(v) => setVolumes((s) => ({ ...s, [stem.key]: v }))}
                  muted={!!muted[stem.key]}
                  onToggleMute={() => setMuted((s) => ({ ...s, [stem.key]: !s[stem.key] }))}
                  soloed={!!soloed[stem.key]}
                  onToggleSolo={() => setSoloed((s) => ({ ...s, [stem.key]: !s[stem.key] }))}
                  canvasRef={(el) => { canvasRefs.current[stem.key] = el; }}
                  isDark={isDark}
                />
              ))}
            </Box>

            {/* Master transport */}
            <Box
              bg="mg.surface"
              borderRadius="xl"
              border="1px solid"
              borderColor="mg.line"
              px={{ base: 4, md: 6 }}
              py={4}
            >
              <HStack spacing={4} align="center">
                <IconButton
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={togglePlayPause}
                  bg="hot.500"
                  color="white"
                  _hover={{ bg: 'hot.600' }}
                  _active={{ bg: 'hot.700' }}
                  borderRadius="full"
                  size="lg"
                  icon={
                    <span aria-hidden style={{ fontSize: 20, lineHeight: 1 }}>
                      {isPlaying ? '❚❚' : '▶'}
                    </span>
                  }
                />
                <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }} color="mg.fg" minW="42px">
                  {fmtTime(playhead)}
                </Text>
                <Slider
                  aria-label="Playhead"
                  value={duration ? (playhead / duration) * 100 : 0}
                  onChange={(v) => seekTo((v / 100) * duration)}
                  flex="1"
                  min={0}
                  max={100}
                  step={0.1}
                  focusThumbOnChange={false}
                >
                  <SliderTrack bg="mg.line2" h="3px">
                    <SliderFilledTrack bg={hotAccent} />
                  </SliderTrack>
                  <SliderThumb boxSize={4} borderColor="hot.500" />
                </Slider>
                <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }} color="mg.muted" minW="42px" textAlign="right">
                  {fmtTime(duration)}
                </Text>
                {/* MIDI download — only when Mureka returned a MIDI ZIP
                    (audio-separation-2 and -3 do; -1 doesn't). Opens in a
                    new tab; Mureka's CDN sets Content-Disposition. */}
                {job?.stems?.midiZipUrl && (
                  <Button
                    as="a"
                    href={job.stems.midiZipUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    size="sm"
                    variant="outline"
                    borderColor="mg.line2"
                    color="mg.fg"
                    _hover={{ bg: 'mg.surfaceHi' }}
                    leftIcon={<span aria-hidden style={{ fontSize: 14 }}>⤓</span>}
                    aria-label="Download MIDI stems"
                  >
                    MIDI
                  </Button>
                )}
              </HStack>
              <Text fontSize="xs" color="mg.dim" mt={2} textAlign="center">
                Nothing is saved unless you export. Studio v1 · mixer only
              </Text>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// ---------------- Sub-components ----------------

function StatusCard({
  tone = 'default',
  children,
}: {
  tone?: 'default' | 'error';
  children: React.ReactNode;
}) {
  return (
    <Center
      minH="240px"
      bg="mg.surface"
      borderRadius="xl"
      border="1px solid"
      borderColor={tone === 'error' ? 'red.400' : 'mg.line'}
      px={6}
      py={8}
      color={tone === 'error' ? 'red.500' : 'mg.fg'}
    >
      <Box>{children}</Box>
    </Center>
  );
}

type StemModel = 'audio-separation-1' | 'audio-separation-2' | 'audio-separation-3';

const MODEL_OPTIONS: Array<{ key: StemModel; short: string; long: string; hasMidi: boolean }> = [
  { key: 'audio-separation-3', short: '2 stems',   long: 'vocals + accompaniment · karaoke-ready',                                  hasMidi: true  },
  { key: 'audio-separation-1', short: '5 stems',   long: 'instrumental · vocals · drums · bass · other',                            hasMidi: false },
  { key: 'audio-separation-2', short: '12 stems',  long: 'vocals · drums · bass · guitar · piano · strings · brass · synth · …',    hasMidi: true  },
];

function SplitCta({
  onSplit,
  isSubmitting,
  failedReason,
  error,
  pickedModel,
  onPickModel,
}: {
  onSplit: () => void;
  isSubmitting: boolean;
  failedReason: string | null | undefined;
  error: string | null;
  pickedModel: StemModel;
  onPickModel: (m: StemModel) => void;
}) {
  const active = MODEL_OPTIONS.find((o) => o.key === pickedModel) || MODEL_OPTIONS[2];
  return (
    <Center
      minH="360px"
      bg="mg.surface"
      borderRadius="2xl"
      border="1px solid"
      borderColor="mg.line"
      px={6}
      py={10}
    >
      <VStack spacing={5} textAlign="center" maxW="520px">
        <Box fontSize="52px" lineHeight={1}>🎛️</Box>
        <VStack spacing={2}>
          <Text fontSize="xl" fontWeight={700}>Open in Studio</Text>
          <Text fontSize="sm" color="mg.muted">
            Split this song into stems, then remix the levels like a mini DAW.
          </Text>
        </VStack>

        {/* Depth picker — segmented control. Solo option highlighted with
            the hot accent; text under it explains what each level yields. */}
        <Box w="100%">
          <HStack
            spacing={0}
            borderRadius="full"
            border="1px solid"
            borderColor="mg.line2"
            overflow="hidden"
            bg="mg.surfaceHi"
          >
            {MODEL_OPTIONS.map((opt) => {
              const isActive = opt.key === pickedModel;
              return (
                <Button
                  key={opt.key}
                  onClick={() => onPickModel(opt.key)}
                  flex="1"
                  borderRadius={0}
                  border="none"
                  bg={isActive ? 'hot.500' : 'transparent'}
                  color={isActive ? 'white' : 'mg.fg'}
                  _hover={{ bg: isActive ? 'hot.600' : 'mg.surface' }}
                  fontSize="13px"
                  fontWeight={600}
                  size="sm"
                >
                  {opt.short}
                </Button>
              );
            })}
          </HStack>
          <Text fontSize="xs" color="mg.muted" mt={2}>
            {active.long}{active.hasMidi ? ' · MIDI included' : ''}
          </Text>
        </Box>

        <Button
          onClick={onSplit}
          bg="hot.500"
          color="white"
          _hover={{ bg: 'hot.600' }}
          _active={{ bg: 'hot.700' }}
          borderRadius="full"
          px={7}
          size="lg"
          isLoading={isSubmitting}
          loadingText="Starting…"
        >
          Split into {active.short} · 1 credit
        </Button>
        {failedReason && (
          <Text fontSize="xs" color="red.500" mt={1}>
            Last attempt failed: {failedReason}
          </Text>
        )}
        {error && (
          <Text fontSize="xs" color="red.500" mt={1}>
            {error}
          </Text>
        )}
        <Text fontSize="xs" color="mg.dim">
          Takes 20–60 seconds. You'll only be charged if the split succeeds.
        </Text>
      </VStack>
    </Center>
  );
}

function ProcessingCard({ requestedAt }: { requestedAt: any }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const anchor = requestedAt ? new Date(requestedAt).getTime() : Date.now();
    const tick = () => setElapsed(Math.floor((Date.now() - anchor) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [requestedAt]);
  return (
    <Center
      minH="320px"
      bg="mg.surface"
      borderRadius="2xl"
      border="1px solid"
      borderColor="mg.line"
      px={6}
      py={10}
    >
      <VStack spacing={5} textAlign="center" maxW="380px">
        <Spinner size="xl" thickness="3px" color="hot.500" />
        <VStack spacing={2}>
          <Text fontSize="xl" fontWeight={700}>Splitting your song</Text>
          <Text fontSize="sm" color="mg.muted">
            Isolating up to 12 individual tracks. This usually takes 20–60 seconds.
          </Text>
          <Text fontSize="xs" color="mg.dim" mt={1}>Elapsed {fmtTime(elapsed)}</Text>
        </VStack>
      </VStack>
    </Center>
  );
}

function ChannelStrip({
  stem,
  volume,
  onVolume,
  muted,
  onToggleMute,
  soloed,
  onToggleSolo,
  canvasRef,
  isDark,
}: {
  stem: DecodedStem;
  volume: number;
  onVolume: (v: number) => void;
  muted: boolean;
  onToggleMute: () => void;
  soloed: boolean;
  onToggleSolo: () => void;
  canvasRef: (el: HTMLCanvasElement | null) => void;
  isDark: boolean;
}) {
  const accent = stem.hue;
  // Lightly desaturate the accent for the dot indicator in dark mode so
  // it doesn't feel neon; keep the waveform + slider vivid.
  const dotColor = isDark ? accent : accent;
  return (
    <Box
      bg="mg.surface"
      borderRadius="xl"
      border="1px solid"
      borderColor="mg.line"
      overflow="hidden"
    >
      <HStack
        px={3}
        py={2}
        borderBottom="1px solid"
        borderColor="mg.line"
        bg="mg.surfaceHi"
        spacing={2}
      >
        <Box fontSize="16px" lineHeight={1}>{stem.icon}</Box>
        <Text fontSize="xs" fontWeight={600} flex="1" noOfLines={1}>{stem.label}</Text>
        <Box
          w="7px"
          h="7px"
          borderRadius="full"
          bg={muted ? 'gray.400' : dotColor}
          opacity={muted ? 0.5 : 1}
        />
      </HStack>

      <Box px={3} py={3}>
        <Box
          as="canvas"
          ref={canvasRef}
          w="100%"
          h="56px"
          display="block"
          borderRadius="md"
          bg="mg.surfaceLo"
        />
      </Box>

      <VStack spacing={2} px={3} pb={3}>
        <Slider
          aria-label={`${stem.label} volume`}
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={onVolume}
          focusThumbOnChange={false}
        >
          <SliderTrack bg="mg.line2" h="3px">
            <SliderFilledTrack bg={accent} />
          </SliderTrack>
          <SliderThumb boxSize={3.5} borderColor={accent} />
        </Slider>
        <HStack spacing={1.5} w="100%">
          <Button
            size="xs"
            flex="1"
            variant={muted ? 'solid' : 'outline'}
            bg={muted ? 'red.500' : undefined}
            color={muted ? 'white' : 'mg.fg'}
            borderColor="mg.line2"
            _hover={{ bg: muted ? 'red.600' : 'mg.surfaceHi' }}
            onClick={onToggleMute}
            aria-pressed={muted}
            fontSize="10px"
          >
            M
          </Button>
          <Button
            size="xs"
            flex="1"
            variant={soloed ? 'solid' : 'outline'}
            bg={soloed ? 'yellow.400' : undefined}
            color={soloed ? 'black' : 'mg.fg'}
            borderColor="mg.line2"
            _hover={{ bg: soloed ? 'yellow.500' : 'mg.surfaceHi' }}
            onClick={onToggleSolo}
            aria-pressed={soloed}
            fontSize="10px"
          >
            S
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
